import {Text, TextField} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {
  CurrencyType,
  DiscountReward,
  PromotionCondition,
  PromotionType,
  RewardType,
  type DiscountType,
} from '@/types/promotion'
import {DiscountType as DiscountTypeEnum} from '@/types/promotionTypes.js'
import {FC} from 'react'
import {useRewardValidations} from './RewardValidations.js'
import {useSelector} from 'react-redux'

type DiscountInputProps = {
  promotionCondition: PromotionCondition
  textBeforeInput?: string
  textAfterInput?: string
  showDynamicPlaceholder?: boolean
  discountType?: DiscountType
  index?: number
}

export const RewardDiscount: FC<DiscountInputProps> = ({
  promotionCondition,
  textAfterInput = '% off.',
  showDynamicPlaceholder = false,
  discountType,
}) => {
  const {updateCondition, promotionConfig, promotion} = usePromotion()
  const maxDiscountPercentage = promotionConfig?.maxDiscountPercentage || 0
  const {countryCode} = useSelector((state: any) => state?.countryCode)
  const isIndia = countryCode == 'IND'
  const textBeforeInput = CurrencyType[countryCode ?? 'MX'] ?? '$'
  const {rewards} = promotionCondition
  const currentReward = rewards?.[0]

  const isDiscountReward = currentReward?.type === RewardType.DISCOUNT
  const currentDiscountReward = isDiscountReward ? (currentReward as DiscountReward) : undefined

  const isCashDiscount = currentDiscountReward?.discountType === DiscountTypeEnum.AMOUNT
  const percentageDiscountTypes = new Set([
    DiscountTypeEnum.PERCENTAGE,
    PromotionType.STORE_DISCOUNT,
    PromotionType.WELCOME_DISCOUNT,
  ])
  const isPercentageDiscount = percentageDiscountTypes.has(
    currentDiscountReward?.discountType ?? DiscountTypeEnum.PERCENTAGE
  )
  const currentRewardDiscountPercentage = currentDiscountReward?.discountValue || ''
  const currentRewardDiscountAmountUpto = currentDiscountReward?.discountAmountUpto || ''
  const {hasValueLayerError} = useRewardValidations(promotionCondition)
  const resolvedDiscountType = (discountType ??
    currentDiscountReward?.discountType ??
    DiscountTypeEnum.PERCENTAGE) as DiscountType

  const updateDiscountReward = (field: 'discountValue' | 'discountAmountUpto', value: string) => {
    const baseReward: DiscountReward = currentDiscountReward ?? {
      type: RewardType.DISCOUNT,
      discountType: resolvedDiscountType,
      discountValue: 0,
    }

    const maxDiscountAmountUptoUpdate = value === '' ? {discountAmountUpto: null}: {discountAmountUpto: Number(value)}

    const rewardFieldUpdate =
      field === 'discountValue'
        ? {discountValue: Number(value)}
        : maxDiscountAmountUptoUpdate

    const nextReward: DiscountReward = {
      ...baseReward,
      discountType: resolvedDiscountType,
      ...rewardFieldUpdate,
    }

    updateCondition({
      ...promotionCondition,
      rewards: [nextReward],
    })
  }

  const limitToTwoDecimals = (value: string) => {
    if (!value) return value

    const normalizedValue = value.replace(',', '.')
    const [integerPart, decimalPart] = normalizedValue.split('.')

    if (decimalPart === undefined) {
      return normalizedValue
    }

    return `${integerPart}.${decimalPart.slice(0, 2)}`
  }

  const formatDisplayValue = (value: string | number) => limitToTwoDecimals(value.toString())

  const createDiscountChangeHandler = (field: 'discountValue' | 'discountAmountUpto') => value => {    
    if (value === '') {
      updateDiscountReward(field, value)
      return
    }

    const normalizedValue = limitToTwoDecimals(value)
    updateDiscountReward(field, normalizedValue)
  }

  const handleDiscountChange = createDiscountChangeHandler('discountValue')
  const handleMaxDiscountChange = createDiscountChangeHandler('discountAmountUpto')

  const placeholderText = showDynamicPlaceholder && !isCashDiscount ? `Up to ${maxDiscountPercentage}%` : '0.0'
  const discountPercentage = currentRewardDiscountPercentage || ''

  const decimalLimit = isIndia ? 2 : 4

  // Don't show error for STORE_DISCOUNT and WELCOME_DISCOUNT promotion types
  const isError =
    promotion.promotionType !== PromotionType.STORE_DISCOUNT &&
    promotion.promotionType !== PromotionType.WELCOME_DISCOUNT &&
    discountType !== DiscountTypeEnum.AMOUNT && 
    discountPercentage > maxDiscountPercentage ||
    (typeof discountPercentage === 'number' && discountPercentage < 0.01)
  const isLayerError = hasValueLayerError()
  const errorText = isLayerError ? 'Value Invalid' : isError ? ' ' : ''
  // @ts-expect-error
  const maxDiscountError = currentRewardDiscountAmountUpto < 0 ? ' ' : ''
  const className = isError || maxDiscountError ? 'disable-error-icon' : ''

  return (
    <>
      {isCashDiscount && <Text>{textBeforeInput}</Text>}
      <TextField
        updateValue={handleDiscountChange}
        value={formatDisplayValue(discountPercentage)}
        error={errorText}
        maxDecimals={decimalLimit}
        uiSchema={{
          helperText: '',
          placeholder: placeholderText,
          className: className,
        }}
        // @ts-expect-error TextField style typing does not include width
        style={{width: '150px'}}
        type="number"
      />
      {!isCashDiscount && <Text>{textAfterInput}</Text>}
      {isPercentageDiscount && (
        <>
          <Text>Max Discount Value $ </Text>
          <TextField
            updateValue={handleMaxDiscountChange}
            value={currentRewardDiscountAmountUpto.toString().replace(',', '.')}
            error={maxDiscountError}
            uiSchema={{
              helperText: '',
              className: className,
            }}
            // @ts-expect-error TextField style typing does not include width
            style={{width: '150px'}}
            type="number"
            min={0}
          />
        </>
      )}
    </>
  )
}
